import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useApiData } from "../hooks/useApiData";
import "./PageContent.css";
import "./GalleryPage.css";
import BackToTopButton from "../components/BackToTopButton";

// "College" -> "college"   "Rashtriya Ekta Diwas" -> "rashtriya-ekta-diwas"
const slugifyCategory = (str) =>
  String(str || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export default function GalleryPage() {
  // Route is /gallery/:catSlug? — catSlug is undefined on plain /gallery
  const { catSlug } = useParams();
  const navigate = useNavigate();

  // GET /api/gallery-categories → tbl_gallerycat
  // Shape per item: { gcat_id, gcat_name, seq }
  const { data: categories, loading: loadingCategories } =
    useApiData("gallery-categories");

  // GET /api/gallery-photos → tbl_photogallery, already filtered to
  // status = 1 (published) and photo_image already comes back as a full,
  // ready-to-use URL (backend prefixes it with SITE_BASE, same as
  // tbl_links.link_logo).
  // Shape per item: { photo_id, gcat_id, photo_caption, photo_image, status }
  const { data: photos, loading: loadingPhotos } = useApiData("gallery-photos");

  const { data: leaders } = useApiData("leadership");

  const [lightboxItem, setLightboxItem] = useState(null);

  const principal = leaders?.find((l) =>
    String(l.role || "").toLowerCase().match(/principal|dean/)
  );
  const medicalSuperintendent = leaders?.find((l) =>
    String(l.role || "").toLowerCase().includes("medical superintendent")
  );

  const slugify = (str) =>
    String(str || "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  const renderOfficialMessage = (leader, title) => {
    if (!leader) {
      return null;
    }

    return (
      <div className="official-message-block">
        <div className="official-message-title">{title}</div>

        <div className="official-message-body">
          <div className="official-message-top">
            <div className="official-photo-wrapper">
              <img
                src={leader.img}
                alt={leader.name}
                className="official-photo"
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    leader.name || "Official"
                  )}&background=0b5d7a&color=fff&size=150`;
                }}
              />
            </div>

            <div className="official-message-text">
              <p>{leader.message}</p>
            </div>
          </div>

          <div className="official-name">{leader.name}</div>

          <div className="official-role">{leader.role}</div>

          <div className="official-more-wrapper">
            <Link
              to={`/leadership-message/${slugify(leader.role)}`}
              className="official-more"
            >
              More...
            </Link>
          </div>
        </div>
      </div>
    );
  };

  // Sort by seq (ascending); several rows share the same seq in the real
  // data, so gcat_id is used as a stable tiebreaker.
  const sortedCategories = [...(categories || [])].sort((a, b) => {
    const seqDiff = (Number(a.seq) || 0) - (Number(b.seq) || 0);
    if (seqDiff !== 0) return seqDiff;
    return (Number(a.gcat_id) || 0) - (Number(b.gcat_id) || 0);
  });

  // A category only gets a tab if it actually has at least one published
  // photo — otherwise clicking it would just land on an empty gallery.
  const loading = loadingCategories || loadingPhotos;
  const categoriesWithPhotos = loading
    ? []
    : sortedCategories.filter((cat) =>
        (photos || []).some((p) => String(p.gcat_id) === String(cat.gcat_id))
      );

  const activeCategory = catSlug
    ? categoriesWithPhotos.find(
        (cat) => slugifyCategory(cat.gcat_name) === catSlug
      )
    : null;

  // Unknown slug, or a category with no photos, once data has loaded —
  // drop back to the plain list instead of showing a dead empty state.
  useEffect(() => {
    if (!loading && catSlug && !activeCategory) {
      navigate("/gallery", { replace: true });
    }
  }, [loading, catSlug, activeCategory, navigate]);

  const activePhotos = activeCategory
    ? (photos || []).filter(
        (p) => String(p.gcat_id) === String(activeCategory.gcat_id)
      )
    : [];

  const handleCategoryClick = (cat) => {
    navigate(`/gallery/${slugifyCategory(cat.gcat_name)}`);
  };

  const renderGalleryItem = (item) => (
    <button
      key={item.photo_id}
      type="button"
      className="gallery-item"
      onClick={() => setLightboxItem(item)}
    >
      <div className="gallery-thumb-wrapper">
        <img
          src={item.photo_image}
          alt={item.photo_caption || "Gallery photo"}
          className="gallery-thumb"
          loading="lazy"
          onError={(e) => {
            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
              item.photo_caption || "Photo"
            )}&background=0b5d7a&color=fff&size=200`;
          }}
        />
        <span className="gallery-zoom-icon">
          <i className="fa fa-search-plus"></i>
        </span>
      </div>

      {item.photo_caption && (
        <div className="gallery-caption">{item.photo_caption}</div>
      )}
    </button>
  );

  return (
    <main className="content-page-wrapper">
      <div className="content-breadcrumb-wrapper">
        <div className="content-container">
          <div className="content-breadcrumb">
            <Link to="/">
              <i className="fa fa-home"></i>
              Home
            </Link>

            <span className="breadcrumb-arrow">
              <i className="fa fa-angle-right"></i>
            </span>

            {activeCategory ? (
              <>
                <Link to="/gallery">Gallery</Link>

                <span className="breadcrumb-arrow">
                  <i className="fa fa-angle-right"></i>
                </span>

                <span className="breadcrumb-current">
                  {activeCategory.gcat_name}
                </span>
              </>
            ) : (
              <span className="breadcrumb-current">Gallery</span>
            )}
          </div>
        </div>
      </div>

      <div className="content-container">
        <div className="content-layout">
          <aside className="content-sidebar">
            <div className="official-info-card">
              <div className="official-info-header">
                <div className="official-info-icon">
                  <i className="fa fa-envelope-o"></i>
                </div>
                <span>MESSAGE FROM</span>
              </div>

              <div className="official-info-body">
                {renderOfficialMessage(principal, "DEAN AND PRINCIPAL")}
                {renderOfficialMessage(
                  medicalSuperintendent,
                  "MEDICAL SUPERINTENDENT"
                )}
              </div>
            </div>
          </aside>

          <section className="content-main-card">
            <h1 className="notice-page-title">Gallery</h1>

            {/* Category tabs — always visible at the top of the page.
                Only categories with at least one published photo get a
                tab, so we wait on both categories and photos loading
                before deciding what to show. */}
            {loading ? (
              <div className="content-loading">
                <div className="content-spinner"></div>
                <p>Loading categories...</p>
              </div>
            ) : categoriesWithPhotos.length > 0 ? (
              <div className="gallery-cat-tabs">
                {categoriesWithPhotos.map((cat) => {
                  const isActive = activeCategory?.gcat_id === cat.gcat_id;
                  return (
                    <button
                      key={cat.gcat_id}
                      type="button"
                      className={`gallery-cat-tab ${isActive ? "active" : ""}`}
                      onClick={() => handleCategoryClick(cat)}
                    >
                      {cat.gcat_name}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="notice-card-empty">No gallery categories found.</p>
            )}

            {/* Selected category's photos — renders below the table on the
                same page as soon as a row is clicked */}
            <div className="gallery-detail-section">
              {!activeCategory ? (
                <p className="notice-card-empty">
                  Select a category above to view photos.
                </p>
              ) : (
                <>
                  <h2 className="gallery-detail-title">
                    {activeCategory.gcat_name}
                  </h2>

                  {loadingPhotos ? (
                    <div className="content-loading">
                      <div className="content-spinner"></div>
                      <p>Loading photos...</p>
                    </div>
                  ) : activePhotos.length > 0 ? (
                    <div className="gallery-grid">
                      {activePhotos.map((item) => renderGalleryItem(item))}
                    </div>
                  ) : (
                    <p className="notice-card-empty">No photos found.</p>
                  )}
                </>
              )}
            </div>
          </section>
        </div>
      </div>

      {lightboxItem && (
        <div
          className="gallery-lightbox-overlay"
          onClick={() => setLightboxItem(null)}
        >
          <div
            className="gallery-lightbox-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="gallery-lightbox-close"
              onClick={() => setLightboxItem(null)}
              aria-label="Close"
            >
              <i className="fa fa-times"></i>
            </button>

            <img
              src={lightboxItem.photo_image}
              alt={lightboxItem.photo_caption || "Gallery photo"}
              className="gallery-lightbox-img"
            />

            {lightboxItem.photo_caption && (
              <div className="gallery-lightbox-caption">
                {lightboxItem.photo_caption}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Back to top button */}
<BackToTopButton />

    </main>
  );
}
